import { useState, useCallback, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { Ruler, Trash2 } from 'lucide-react';
import type { ImageMeasurement } from '@/hooks/useToothImages';

interface MeasurementToolProps {
  measurements: ImageMeasurement[];
  onChange: (measurements: ImageMeasurement[]) => void;
  imageWidth: number;
  imageHeight: number;
  active: boolean;
  onToggleActive: () => void;
}

/**
 * Click-two-points ruler measurement tool drawn as an SVG overlay.
 * Shows pixel-distance. Always shows the uncalibrated disclaimer.
 */
export function MeasurementTool({
  measurements,
  onChange,
  imageWidth,
  imageHeight,
  active,
  onToggleActive,
}: MeasurementToolProps) {
  const { t } = useTranslation();
  const [pendingFirst, setPendingFirst] = useState<[number, number] | null>(null);
  const svgRef = useRef<SVGSVGElement>(null);

  const getSvgPoint = useCallback(
    (e: React.MouseEvent<SVGSVGElement>): [number, number] => {
      const rect = svgRef.current!.getBoundingClientRect();
      return [
        ((e.clientX - rect.left) / rect.width) * imageWidth,
        ((e.clientY - rect.top) / rect.height) * imageHeight,
      ];
    },
    [imageWidth, imageHeight],
  );

  const handleClick = useCallback(
    (e: React.MouseEvent<SVGSVGElement>) => {
      if (!active) return;
      const pt = getSvgPoint(e);

      if (!pendingFirst) {
        setPendingFirst(pt);
      } else {
        const dx = pt[0] - pendingFirst[0];
        const dy = pt[1] - pendingFirst[1];
        const dist = Math.sqrt(dx * dx + dy * dy);

        const newMeasurement: ImageMeasurement = {
          id: crypto.randomUUID(),
          kind: 'length',
          points: [...pendingFirst, ...pt],
          value_px: Math.round(dist),
          value_mm: null,
          calibrated: false,
        };

        onChange([...measurements, newMeasurement]);
        setPendingFirst(null);
      }
    },
    [active, getSvgPoint, pendingFirst, measurements, onChange],
  );

  const handleDelete = (id: string) => {
    onChange(measurements.filter((m) => m.id !== id));
  };

  const scaleX = imageWidth > 0 ? 100 / imageWidth : 1;
  const scaleY = imageHeight > 0 ? 100 / imageHeight : 1;

  return (
    <>
      {/* SVG overlay */}
      <svg
        ref={svgRef}
        className="pointer-events-none absolute inset-0 h-full w-full"
        style={{ pointerEvents: active ? 'all' : 'none', cursor: active ? 'crosshair' : 'default' }}
        viewBox={`0 0 100 100`}
        preserveAspectRatio="none"
        onClick={handleClick}
      >
        {measurements.map((m) => {
          if (m.kind !== 'length' || m.points.length < 4) return null;
          const [x1, y1, x2, y2] = m.points.map((v, i) => v * (i % 2 === 0 ? scaleX : scaleY));
          const midX = (x1 + x2) / 2;
          const midY = (y1 + y2) / 2;
          return (
            <g key={m.id}>
              <line x1={x1} y1={y1} x2={x2} y2={y2} stroke="#facc15" strokeWidth="0.4" />
              <circle cx={x1} cy={y1} r="0.8" fill="#facc15" />
              <circle cx={x2} cy={y2} r="0.8" fill="#facc15" />
              <text
                x={midX}
                y={midY - 1.5}
                fontSize="3"
                fill="#facc15"
                textAnchor="middle"
                style={{ userSelect: 'none' }}
              >
                {m.value_px}px
              </text>
            </g>
          );
        })}

        {/* Pending first point */}
        {pendingFirst && (
          <circle
            cx={pendingFirst[0] * scaleX}
            cy={pendingFirst[1] * scaleY}
            r="0.8"
            fill="#facc15"
          />
        )}
      </svg>

      {/* Disclaimer + controls */}
      {(active || measurements.length > 0) && (
        <div className="absolute bottom-10 left-2 z-10 flex flex-col gap-1">
          <div className="rounded-lg bg-amber-900/80 px-3 py-1.5 text-xs text-amber-200 backdrop-blur-sm">
            {t('dental.imaging.lightbox.notCalibrated')}
          </div>
          {measurements.map((m) => (
            <div
              key={m.id}
              className="flex items-center gap-2 rounded-lg bg-black/60 px-2 py-1 text-xs text-white"
            >
              <Ruler className="h-3 w-3 text-yellow-400" />
              <span>{m.value_px}px</span>
              <button
                onClick={() => handleDelete(m.id)}
                className="ml-auto text-ds-red hover:text-red-300"
              >
                <Trash2 className="h-3 w-3" />
              </button>
            </div>
          ))}
        </div>
      )}

      {/* Toggle button — rendered by parent toolbar, exposed here as a helper */}
      <button
        onClick={onToggleActive}
        title={t('dental.imaging.lightbox.zoom')}
        className={`rounded-lg p-2 transition-colors ${
          active
            ? 'bg-ds-amber text-ds-text'
            : 'bg-slate-800 text-ds-placeholder hover:bg-slate-700 hover:text-white'
        }`}
      >
        <Ruler className="h-4 w-4" />
      </button>
    </>
  );
}
